import { h, render } from 'preact'
import Icon from 'snippets/icon/icon'

/**
 * Back to top button — visible after one third of the scrollable page height.
 * Visibility is driven by data-ui="visible" and data-visible: Tailwind variants.
 * Once the footer row marked [data-back-to-top-anchor] scrolls into view, the
 * button docks to it (rides up with it) instead of overlapping the bottom bar.
 */
export class BackToTopElement extends HTMLElement {
  static SCROLL_FRACTION = 2 / 5
  static ANCHOR_SELECTOR = '[data-back-to-top-anchor]'
  static DOCK_GAP = 24
  static BUTTON_CLASS =
    'btn btn-fill btn-primary btn-m fixed bottom-margin left-margin z-40 opacity-0 invisible pointer-events-none transition-opacity data-visible:opacity-100 data-visible:visible data-visible:pointer-events-auto'

  private button: HTMLButtonElement | null = null
  private anchor: HTMLElement | null = null
  private ticking = false
  private visible: boolean | null = null
  private dockOffset: number | null = null
  private readonly boundHandleScroll = () => this.handleScroll()
  private readonly boundHandleResize = () => this.update()
  private readonly boundHandleClick = () => this.scrollToTop()

  connectedCallback() {
    const label = this.textContent?.trim() || this.getAttribute('label') || 'Back To Top'
    this.textContent = ''
    this.renderButton(label)
    this.update()
    window.addEventListener('scroll', this.boundHandleScroll, { passive: true })
    window.addEventListener('resize', this.boundHandleResize, { passive: true })
    this.button?.addEventListener('click', this.boundHandleClick)
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.boundHandleScroll)
    window.removeEventListener('resize', this.boundHandleResize)
    this.button?.removeEventListener('click', this.boundHandleClick)
  }

  private get scrollThreshold(): number {
    return Math.max(0, window.innerHeight * 2)
  }

  private renderButton(label: string) {
    this.button = document.createElement('button')
    this.visible = null
    this.dockOffset = null
    this.button.type = 'button'
    this.button.className = BackToTopElement.BUTTON_CLASS
    this.button.setAttribute('aria-label', label)
    this.button.setAttribute('aria-hidden', 'true')

    const iconWrap = document.createElement('span')
    iconWrap.className = 'flex size-[14px] children:size-full'
    render(h(Icon, { id: 'arrow-up', className: 'size-full' }), iconWrap)
    this.button.appendChild(iconWrap)

    const labelText = document.createElement('span')
    labelText.textContent = label
    this.button.appendChild(labelText)

    this.appendChild(this.button)
  }

  private get dockAnchor(): HTMLElement | null {
    if (!this.anchor?.isConnected) {
      this.anchor = document.querySelector(BackToTopElement.ANCHOR_SELECTOR)
    }
    return this.anchor
  }

  private handleScroll() {
    if (this.ticking) return
    this.ticking = true
    window.requestAnimationFrame(() => {
      this.update()
      this.ticking = false
    })
  }

  private update() {
    this.updateVisibility()
    this.updateDockOffset()
  }

  /**
   * Keep the button above the footer anchor: once the anchor row approaches,
   * shift the button up so its bottom edge stays DOCK_GAP above the row's top.
   */
  private updateDockOffset() {
    if (!this.button) return

    const anchor = this.dockAnchor
    if (!anchor) return

    const baseBottom = parseFloat(window.getComputedStyle(this.button).bottom) || 0
    const fixedBottomEdge = window.innerHeight - baseBottom
    const dockedBottomEdge = anchor.getBoundingClientRect().top - BackToTopElement.DOCK_GAP
    const offset = Math.min(0, dockedBottomEdge - fixedBottomEdge)
    if (offset === this.dockOffset) return
    this.dockOffset = offset

    this.button.style.transform = offset ? `translateY(${offset}px)` : ''
  }

  private updateVisibility() {
    if (!this.button) return

    const visible = window.scrollY >= this.scrollThreshold
    // Unchanged writes still emit mutation records, which session replay records per frame.
    if (visible === this.visible) return
    this.visible = visible
    if (visible) {
      this.button.dataset.ui = 'visible'
      this.button.setAttribute('aria-hidden', 'false')
    } else {
      delete this.button.dataset.ui
      this.button.setAttribute('aria-hidden', 'true')
    }
  }

  private scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}
